import { Client } from "@stomp/stompjs";

// http(s) 또는 상대 경로를 ws(s) 절대 주소로 바꾼다.
// SockJS 폴백(xhr-polling)은 요청마다 다른 Cloud Run 인스턴스로 갈 수 있어 STOMP 세션이 깨지므로 순수 WebSocket 만 쓴다.
const toWsUrl = (url) => {
  const abs = new URL(url, window.location.href);
  abs.protocol = abs.protocol === "https:" ? "wss:" : "ws:";
  return abs.toString();
};

const stomp = (() => {
  let client = null;
  let connected = false;
  let connecting = null; // { promise, resolve, reject }
  let connectTimeoutId = null;
  let config = {
    wsUrl: "/ws",
    connectHeaders: {},
    reconnectDelay: 5000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    connectionTimeoutMs: 10000,
  };

  function resolveConnectHeaders() {
    const h = config.connectHeaders;
    try {
      if (typeof h === "function") return h() || {};
      return h || {};
    } catch (e) {
      console.warn("[STOMP] connectHeaders resolver error", e);
      return {};
    }
  }

  // 재접속 시 자동 재구독
  const desiredSubs = new Map(); // 원하는 구독 목록을 저장하는 자료 구조 : id -> { destination, callback, headers }
  const activeSubs = new Map(); // 실제로 서버에 연결되어 현재 살아 있는 구독 목록 : id -> stomp subscription
  const onConnectCbs = new Set(); // 연결이 성공했을 때 실행한 콜백 함수들의 집합
  const onCloseCbs = new Set();
  const onErrorCbs = new Set();

  // 새로운 STOMP 클라이언트를 생성
  function makeClient() {
    const c = new Client({
      brokerURL: toWsUrl(config.wsUrl), // 순수 WebSocket 주소 (SockJS 미사용)
      reconnectDelay: config.reconnectDelay, // 자동 재연결 시도 간격 (ms 단위)
      // connectHeaders는 beforeConnect에서 갱신한다.
      // (connectHeaders에 function이 들어오는 케이스를 방지)
      connectHeaders: {},
      heartbeatIncoming: config.heartbeatIncoming,
      heartbeatOutgoing: config.heartbeatOutgoing,
      connectionTimeout: Math.max(0, Number(config.connectionTimeoutMs) || 0),

      // 연결 성공
      beforeConnect: async function () {
        this.connectHeaders = resolveConnectHeaders();
      },

      onConnect: () => {
        // Ignore late events from a previous client instance (e.g. during rapid reconnects).
        if (client !== c) return;
        connected = true;
        if (connectTimeoutId) {
          clearTimeout(connectTimeoutId);
          connectTimeoutId = null;
        }
        if (connecting?.resolve) connecting.resolve();
        connecting = null;

        console.info("[STOMP] 연결 성공 ✅");

        // 재구독 (끊겼던 구독을 복원)
        activeSubs.clear();
        for (const [id, { destination, callback, headers }] of desiredSubs) {
          try {
            const sub = c.subscribe(destination, callback, headers);
            activeSubs.set(id, sub);
          } catch (error) {
            console.error("[STOMP] 재구독 실패");
          }
        }

        // onConnect 리스너 실행
        for (const fn of onConnectCbs) {
          try {
            fn();
          } catch (error) {
            console.error(error);
          }
        }
      },

      // 연결 해제
      onDisconnect: () => {
        // Ignore late events from a previous client instance (e.g. during rapid reconnects).
        if (client !== c) return;
        connected = false;
        activeSubs.clear();
        console.info("[STOMP] 연결 해제");

        // CONNECTED 전에 disconnect/close가 발생하면 connect()가 영원히 pending으로 남을 수 있다.
        if (connecting?.reject) {
          if (connectTimeoutId) {
            clearTimeout(connectTimeoutId);
            connectTimeoutId = null;
          }
          connecting.reject(new Error("STOMP disconnected before CONNECTED"));
          connecting = null;
        }
        for (const fn of onCloseCbs) {
          try {
            fn({ type: "disconnect" });
          } catch (error) {
            console.error(error);
          }
        }
      },

      // 오류 처리
      onStompError: (e) => {
        // Ignore late events from a previous client instance (e.g. during rapid reconnects).
        if (client !== c) return;
        console.error("[STOMP error] ", e);
        if (connectTimeoutId) {
          clearTimeout(connectTimeoutId);
          connectTimeoutId = null;
        }
        connecting?.reject?.(e);
        connecting = null;
        for (const fn of onErrorCbs) {
          try {
            fn(e);
          } catch (error) {
            console.error(error);
          }
        }
      },

      onWebSocketError: (e) => {
        // Ignore late events from a previous client instance (e.g. during rapid reconnects).
        if (client !== c) return;
        console.error("[WebSocket error] ", e);
        if (connectTimeoutId) {
          clearTimeout(connectTimeoutId);
          connectTimeoutId = null;
        }
        connecting?.reject?.(e);
        connecting = null;
        for (const fn of onErrorCbs) {
          try {
            fn(e);
          } catch (error) {
            console.error(error);
          }
        }
      },

      onWebSocketClose: (e) => {
        // Ignore late events from a previous client instance (e.g. during rapid reconnects).
        if (client !== c) return;
        console.warn("[WebSocket closed] ", e);
        connected = false;

        // close 이벤트는 error 없이 발생할 수 있으므로, CONNECTED 전에 닫히면 connect()를 reject 해야 한다.
        if (connecting?.reject) {
          if (connectTimeoutId) {
            clearTimeout(connectTimeoutId);
            connectTimeoutId = null;
          }
          connecting.reject(new Error("WebSocket closed before CONNECTED"));
          connecting = null;
        }
        for (const fn of onCloseCbs) {
          try {
            fn(e);
          } catch (error) {
            console.error(error);
          }
        }
      },
    });

    return c;
  }

  return {
    // STOMP 클라이언트의 설정을 바꾸는 함수
    configure(opts = {}) {
      config = { ...config, ...opts };
    },

    // 현재 연결 상태를 반환 (true / false)
    isConnected() {
      return connected;
    },

    // 서버에 연결 시도
    async connect() {
      if (connected) return;
      if (connecting) return connecting.promise;

      let resolve, reject;

      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });

      connecting = { promise, resolve, reject };

      // activate()가 onConnect를 매우 빠르게 호출해도 resolve를 놓치지 않도록 connecting을 먼저 세팅한다.
      client = makeClient();
      client.activate();

      // STOMP 연결이 성공/실패 콜백을 타지 못하고 멈추는 케이스(네트워크/프록시) 방어.
      const timeoutMs = Math.max(0, Number(config.connectionTimeoutMs) || 0);
      if (timeoutMs > 0) {
        connectTimeoutId = setTimeout(() => {
          if (!connecting) return;
          try {
            connecting.reject(new Error("STOMP connect timeout"));
          } finally {
            connecting = null;
            connectTimeoutId = null;
            try {
              client?.deactivate?.();
            } catch {}
          }
        }, timeoutMs);
      }

      return promise;
    },

    // 연결 해제 (모든 구독 취소 및 리소스 정리)
    async disconnect() {
      desiredSubs.clear();
      for (const sub of activeSubs.values()) {
        try {
          sub.unsubscribe();
        } catch {}
      }
      activeSubs.clear();

      // 실제 클라이언트 연결 해제
      if (client) {
        const c = client;
        client = null;
        connected = false;
        if (connectTimeoutId) {
          clearTimeout(connectTimeoutId);
          connectTimeoutId = null;
        }
        if (connecting?.reject) {
          try {
            connecting.reject(new Error("STOMP disconnected"));
          } catch {}
        }
        connecting = null;

        try {
          await c.deactivate();
          console.info("[STOMP] 연결 해제 후 메모리 정리");
        } catch (e) {
          console.error("[STOMP] 연결 해제 중 에러 발생: ", e);
        }
      }
    },

    // 서버의 특정 채널(destination)을 구독
    subscribe(destination, callback, headers) {
      const id = Symbol(destination);
      desiredSubs.set(id, { destination, callback, headers });

      // 이미 연결되어 있으면 즉시 구독 수행
      if (connected && client) {
        try {
          const sub = client.subscribe(destination, callback, headers);
          activeSubs.set(id, sub);
        } catch (e) {
          console.error("[STOMP] 구독 에러 발생:", e);
        }
      }

      // 구독 해제 함수 반환
      return {
        unsubscribe: () => {
          const sub = activeSubs.get(id);
          if (sub) {
            try {
              sub.unsubscribe();
            } catch {}
            activeSubs.delete(id);
          }
          desiredSubs.delete(id);
        },
      };
    },

    // 서버로 메시지 전송
    publish(destination, body = {}, headers) {
      if (!client || !connected) {
        console.warn("[STOMP] 전송 불가 — 연결되지 않음 ⚠️");
        return;
      }

      const payload = typeof body === "string" ? body : JSON.stringify(body);

      try {
        client.publish({ destination, body: payload, headers });
      } catch (e) {
        console.error("[STOMP] 메시지 전송 중 에러 발생: ", e);
      }
    },

    // 연결이 성공했을 때 실행할 콜백 등록
    onConnect(fn) {
      onConnectCbs.add(fn);
      return () => onConnectCbs.delete(fn);
    },
    onClose(fn) {
      onCloseCbs.add(fn);
      return () => onCloseCbs.delete(fn);
    },
    onError(fn) {
      onErrorCbs.add(fn);
      return () => onErrorCbs.delete(fn);
    },
  };
})();

export default stomp;
